import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupsIcon from "@mui/icons-material/Groups";
import TuneIcon from "@mui/icons-material/Tune";

import EventCategoriesConfigPanel from "../components/EventCategoriesConfigPanel";
import UserAccessConfigPanel from "../components/UserAccessConfigPanel";
import TreasuryAccountsPanel from "../treasury/TreasuryAccountsPanel";
import TreasuryGeneralSettingsPanel from "../treasury/TreasuryGeneralSettingsPanel";
import TreasuryMovementCategoriesPanel from "../treasury/TreasuryMovementCategoriesPanel";
import { useAuth } from "../../../app/providers/AuthProvider";

const CONFIG_SECTIONS = [
  {
    id: "user-access",
    label: "Acceso de usuarios",
    description: "Activación y desactivación de usuarios",
    icon: ManageAccountsIcon,
  },
  {
    id: "event-categories",
    label: "Categorías de eventos",
    description: "Catálogo de labels del calendario",
    icon: LocalOfferIcon,
  },
  {
    id: "treasury-categories",
    label: "Categorías de movimientos",
    description: "Catálogo de categorías de tesorería",
    icon: AccountBalanceWalletIcon,
  },
  {
    id: "treasury-accounts",
    label: "Cuentas de tesorería",
    description: "Catálogo de cuentas activas",
    icon: AccountBalanceIcon,
  },
  {
    id: "treasury-settings",
    label: "Configuración de tesorería",
    description: "Movimiento predeterminado",
    icon: TuneIcon,
  },
];

const CONFIG_GROUPS = [
  {
    id: "rrhh",
    label: "Recursos Humanos",
    icon: GroupsIcon,
    sections: CONFIG_SECTIONS.slice(0, 2),
  },
  {
    id: "treasury",
    label: "Tesorería",
    icon: AccountBalanceWalletIcon,
    sections: CONFIG_SECTIONS.slice(2),
  },
];

export default function ConfiguracionesPage() {
  const { user } = useAuth();
  const canManageUserAccess = ["CONSULTORA", "MANAGER"].includes(
    String(user?.user_role || "").toUpperCase()
  );
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);

  const handleGroupChange = (groupId) => (_, expanded) => {
    setExpandedGroup(expanded ? groupId : null);
    setSelectedSection(null);
  };

  return (
    <Box sx={{ p: 0, minHeight: "100%", bgcolor: "neutral.100" }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={{ xs: "stretch", md: "flex-start" }}
        sx={{ mt: 3 }}
      >
        <Paper
          sx={{
            width: { xs: "100%", md: 260 },
            p: 2,
            borderRadius: "28px",
            bgcolor: "neutral.50",
            boxShadow: "inset 0px 0px 6px rgba(117, 117, 138, 0.35)",
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", mb: 1.5 }}>
            Configuraciones disponibles
          </Typography>

          <Stack spacing={1.2}>
            {CONFIG_GROUPS.map(({ id: groupId, label: groupLabel, icon: GroupIcon, sections }) => (
              <Accordion
                key={groupId}
                expanded={expandedGroup === groupId}
                onChange={handleGroupChange(groupId)}
                disableGutters
                elevation={0}
                sx={{
                  bgcolor: "transparent",
                  border: "1px solid",
                  borderColor: expandedGroup === groupId ? "primary.500" : "secondary.300",
                  borderRadius: "18px !important",
                  overflow: "hidden",
                  "&::before": { display: "none" },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: "primary.900" }} />}
                  sx={{
                    minHeight: 52,
                    px: 1.5,
                    bgcolor:
                      expandedGroup === groupId ? "secondary.100" : "neutral.50",
                    "& .MuiAccordionSummary-content": {
                      my: 1.1,
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GroupIcon sx={{ color: "primary.500", fontSize: 22 }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: "primary.900" }}>
                      {groupLabel}
                    </Typography>
                  </Stack>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 1.2, bgcolor: "neutral.50" }}>
                  <Stack spacing={1}>
                    {sections.filter(({ id }) => id !== "user-access" || canManageUserAccess).map(({ id, label, icon: Icon }) => {
                      const active = selectedSection === id;

                      return (
                        <Button
                          key={id}
                          onClick={() => setSelectedSection(id)}
                          startIcon={<Icon />}
                          variant={active ? "contained" : "outlined"}
                          color={active ? "primary" : "secondary"}
                          sx={{
                            justifyContent: "flex-start",
                            borderRadius: "999px",
                            fontWeight: 800,
                            textTransform: "none",
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          {label}
                        </Button>
                      );
                    })}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Paper>

        <Box sx={{ flex: 1 }}>
          {!selectedSection ? (
            <Paper
              elevation={0}
              sx={{
                minHeight: 220,
                borderRadius: "28px",
                bgcolor: "neutral.50",
                boxShadow: "inset 0px 0px 6px rgba(117, 117, 138, 0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 3,
                py: 5,
              }}
            >
              <Stack spacing={0.8} alignItems="center" textAlign="center">
                <TuneIcon sx={{ color: "secondary.500", fontSize: 38 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
                  Seleccioná una configuración
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "neutral.600" }}>
                  Abrí Recursos Humanos o Tesorería y elegí una opción para comenzar.
                </Typography>
              </Stack>
            </Paper>
          ) : null}

          {canManageUserAccess && selectedSection === "user-access" ? (
            <UserAccessConfigPanel />
          ) : null}

          {selectedSection === "event-categories" ? (
            <EventCategoriesConfigPanel />
          ) : null}

          {selectedSection === "treasury-categories" ? (
            <TreasuryMovementCategoriesPanel />
          ) : null}

          {selectedSection === "treasury-accounts" ? (
            <TreasuryAccountsPanel />
          ) : null}

          {selectedSection === "treasury-settings" ? (
            <TreasuryGeneralSettingsPanel />
          ) : null}
        </Box>
      </Stack>
    </Box>
  );
}
